import { Modal } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { Refresh01 } from 'blaze-ui/icons/20/Refresh01'
import { HoverActionOverlay } from 'components/HoverActionOverlay/HoverActionOverlay'
import { isVideo } from 'components/ImagePickerModal/utils'
import Styles from '../RegenerateDesignModal.module.scss'

interface RefineSettingsProps {
  topic: string
  setTopic: (topic: string) => void
  sourceMediaUrl: string
  error: string | null
  generationCost: number
  isLoading: boolean
  onClose: () => void
  onRegenerate: () => void
  onReplaceImage: () => void
  provider?: string
  hasInsufficientCredits?: boolean
}

const REGENERATE_LABELS: Record<string, string> = {
  veo3: 'AI Video',
  ai_generated: 'Stylized Image',
}

const getRegenerateButtonText = (provider?: string, isLoading?: boolean, hasInsufficientCredits?: boolean): string => {
  if (hasInsufficientCredits) {
    return 'Add credits'
  }
  const label = (provider && REGENERATE_LABELS[provider]) || 'Post'
  return isLoading ? `Regenerating ${label}...` : `Regenerate ${label}`
}

const SourceMediaPreview = ({ url, className }: { url: string; className?: string }) => {
  if (isVideo({ url })) {
    return <video src={url} className={className} autoPlay loop muted playsInline />
  }

  return <img src={url} alt="Source" className={className} />
}

export const RefineSettings = ({
  topic,
  setTopic,
  sourceMediaUrl,
  error,
  generationCost,
  isLoading,
  onClose,
  onRegenerate,
  onReplaceImage,
  provider,
  hasInsufficientCredits,
}: RefineSettingsProps) => {
  const isDisabled = isLoading

  return (
    <>
      <Modal.Header title="Regenerate post" onClose={onClose} />
      <Modal.Content>
        <div className={Styles.reviewContent}>
          <div className={Styles.formSection}>
            <div className={Styles.topicLabel}>
              <Text variant="primary" color="dark-90">
                Revise topic
              </Text>
            </div>
            <textarea
              id="refine-topic"
              className={Styles.textarea}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Enter the topic for refinement..."
              disabled={isDisabled}
            />
          </div>
          <div className={Styles.formSection}>
            <div className={Styles.sourceHeader}>
              <Text variant="primary" color="dark-90">
                Change source media
              </Text>
              <Button variant="tertiary" size="sm" frontIcon={Refresh01} onPress={onReplaceImage}>
                Replace
              </Button>
            </div>
            <div className={Styles.thumbnailContainer}>
              <HoverActionOverlay
                onAction={onReplaceImage}
                buttonLabel="Replace"
                position="center"
                scrimOpacity={40}
                buttonProps={{
                  frontIcon: Refresh01,
                  variant: 'secondary',
                }}
              >
                <SourceMediaPreview url={sourceMediaUrl} className={Styles.thumbnail} />
              </HoverActionOverlay>
            </div>
          </div>
        </div>
        {error && <div className={Styles.error}>{error}</div>}
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button variant="tertiary" size="lg" onPress={onClose} isDisabled={isDisabled}>
            Close
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button size="lg" onPress={onRegenerate} isDisabled={!topic.trim() || isDisabled}>
            <div className={Styles.generationCostContainer}>
              {getRegenerateButtonText(provider, isLoading, hasInsufficientCredits)}
              {!isLoading && generationCost > 0 && (
                <div className={Styles.generationCost}>
                  <Credits /> {generationCost}
                </div>
              )}
            </div>
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
